import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import { useGetArtifactStatsQuery } from '@/services/api/artifactService';
import {
  useGetBlobDetailQuery,
  useRetriggerBlobTransformMutation,
} from '@/services/api/blobService';

type UnreadableBlobItemProps = {
  matterId: string;
  blobId: string;
};

export const UnreadableBlobItem = ({
  matterId,
  blobId,
}: UnreadableBlobItemProps) => {
  const { data, isLoading } = useGetBlobDetailQuery({ matterId, blobId });
  const [retrigger, { isLoading: isRetriggering }] =
    useRetriggerBlobTransformMutation();

  const handleRetriggerClick = async () => {
    try {
      await retrigger({ matterId, blobId }).unwrap();
      toast.success('Transform retriggered');
    } catch (error: any) {
      const message = error?.data?.error || 'Failed to retrigger transform';
      toast.error(message);
    }
  };

  const filename = data?.original_filename || data?.name || blobId;

  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="truncate text-xs" title={filename}>
        {isLoading ? 'Loading…' : filename}
      </div>
      <Button
        aria-label={`Retrigger transform for ${filename}`}
        onClick={handleRetriggerClick}
        disabled={isRetriggering}
        outline
        className="shrink-0"
      >
        {isRetriggering ? 'Retriggering…' : 'Retrigger'}
      </Button>
    </div>
  );
};

export const UnreadableBlobList = ({ matterId }: { matterId: string }) => {
  const { data: stats } = useGetArtifactStatsQuery({ matterId });
  const blobIds = stats?.non_readable_blob_ids || [];
  if (!blobIds.length) return null;

  return (
    <div className="rounded-md border p-3">
      <div className="mb-2 text-xs font-medium text-muted-foreground">
        Unreadable blobs
      </div>
      <div className="max-h-48 overflow-auto divide-y">
        {blobIds.map((blobId) => (
          <UnreadableBlobItem
            key={blobId}
            matterId={matterId}
            blobId={blobId}
          />
        ))}
      </div>
    </div>
  );
};
